import { FC, useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import { CalendarIcon, Table } from '@smartshell/ui-kit';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EPaymentItemType, TTariffGridItem } from 'types';
import { assumeDuration, formatTariffTime } from 'utils';

import { ITariffsFieldProps } from './interfaces';
import { ITariffGridRequest, ITariffGridResponse, TARIFF_GRID } from './schema';
import s from './ShopTariffsTable.module.scss';

export const ShopTariffsTable: FC<ITariffsFieldProps> = ({
  onItemSelect,
  hostId,
  clientUUID,
  shift,
  shouldFilterFixedEndTime,
  search,
  combo,
  isBookingLoading,
  disabledTariff,
  items,
  promoCode,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const COLUMNS: Array<Column<TTariffGridItem>> = useMemo(
    () => [
      {
        Header: t('title'),
        accessor: 'title',
        width: 200,
      },
      {
        Header: t('cost'),
        accessor: 'cost',
        Cell: ({ value: cost }) => {
          return `${cost.toLocaleString()} ${currency.sign}`;
        },
        width: 133,
      },
      {
        Header: t('tariff.from'),
        accessor: 'time_from',
        width: 141,
      },
      {
        Header: t('tariff.to'),
        accessor: 'time_to',
        width: 150,
      },
      {
        Header: t('duration'),
        accessor: ({ duration, has_fixed_finish_time, time_from, time_to }) => {
          if (has_fixed_finish_time) {
            const start = formatTariffTime(time_from);
            let end = formatTariffTime(time_to);

            if (end.isBefore(start)) {
              end = end.add(1, 'day');
            }

            return end.diff(start, 'seconds');
          }

          return duration;
        },
        Cell: ({ value }: { value: number }) =>
          assumeDuration(value, t)?.title ?? null,
        width: 155,
      },

      {
        accessor: 'booked',
        Cell: ({ value }) => value && <CalendarIcon size="s" />,
        width: 48,
        disableSortBy: true,
      },
    ],
    [t]
  );

  const queryVariables = useMemo(
    () => ({
      input: {
        host_id: hostId,
        client_uuid: clientUUID ?? undefined,
        ...(shift ? { shift: shift <= 0 ? 1 : shift } : {}),
      },
    }),
    [hostId, clientUUID, shift]
  );
  const { data, loading } = useQuery<ITariffGridResponse, ITariffGridRequest>(
    TARIFF_GRID,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: queryVariables,
      skip: isBookingLoading,
    }
  );

  const filteredTariffs = useMemo(
    () =>
      data?.tariffGrid.filter((tariff) => {
        if (tariff.per_minute) {
          return false;
        }

        if (!clientUUID && tariff.pausable) return false;

        if (
          items?.some(
            (item) =>
              (item.type === 'TARIFF' &&
                !item.original.disable_sale_receipt_printing) ||
              item.type !== 'TARIFF'
          ) &&
          tariff.disable_sale_receipt_printing
        )
          return false;

        if (shouldFilterFixedEndTime && tariff.has_fixed_finish_time) {
          return false;
        }

        if (
          search &&
          !tariff.title.toLowerCase().includes(search.toLowerCase())
        ) {
          return false;
        }

        return true;
      }),
    [search, data, items]
  );

  return (
    <Table
      disabled={!!combo || disabledTariff || promoCode}
      className={s['tariffs-table']}
      data={filteredTariffs ?? []}
      columns={COLUMNS}
      getRowClassName={({ booked }) => {
        if (booked) return s.booked;
      }}
      onRowClick={(_, row) => {
        const { cost, id, use_global_discounts } = row.original;

        onItemSelect({
          id,
          cost,
          use_global_discounts,
          amount: 1,
          type: EPaymentItemType.TARIFF,
          original: row.original,
        });
      }}
      isLoading={loading || isBookingLoading}
      emptyDataPlaceholder={t('shopForm.empty.tariffs')}
    />
  );
};
